import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getShipperAccountManager, setShipperAccountManager } from '../../api/endpoints/ops';
import { staticQueryPolicy } from '../../api/queryPolicies';
import { useMutationHook } from '../../hooks/useMutationHook';
import { OwnerChip } from './OwnerChip';
import { opsUserName, useOpsUser, useOpsUsers } from './opsUser';
import styles from './AccountManagerSection.module.css';
import { Button } from '../../components/ui/Button';

interface Props {
  /** Shipper user id (== shipper profile id). */
  shipperId: number;
}

/**
 * Shipper account manager: new shipments from this shipper auto-assign to
 * them while they are ACTIVE and under cap. Editable by ops managers only.
 */
export function AccountManagerSection({ shipperId }: Props) {
  const { t } = useTranslation();
  const { opsUser, isManager } = useOpsUser();
  const opsUsers = useOpsUsers(isManager);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState('');
  const [error, setError] = useState<string | null>(null);

  const queryKey = ['admin', 'shippers', 'account-manager', shipperId] as const;
  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => getShipperAccountManager(shipperId),
    ...staticQueryPolicy,
  });

  const save = useMutationHook(
    (opsUserId: number | null) => setShipperAccountManager(shipperId, opsUserId),
    {
      successMessage: (r) =>
        r.accountManager
          ? `Account manager set to ${opsUserName(r.accountManager)}.`
          : 'Account manager removed.',
      invalidateKeys: [['admin', 'shippers', 'account-manager', shipperId]],
      onSuccess: () => {
        setEditing(false);
        setError(null);
      },
      onError: (e) => setError(e.message),
    },
  );

  const current = data?.accountManager ?? null;

  return (
    <div className={styles.section} aria-label={t('ownership.accountManagerSection.label')}>
      <span className={styles.label}>{t('ownership.accountManagerSection.label')}</span>
      <div className={styles.value}>
        {isLoading ? (
          <span className={styles.hint}>{t('ownership.accountManagerSection.loading')}</span>
        ) : isError ? (
          <span className={styles.error}>{t('ownership.accountManagerSection.loadError')}</span>
        ) : editing ? (
          <>
            <select
              aria-label={t('ownership.accountManagerSection.label')}
              className={styles.select}
              value={selected}
              disabled={save.isLoading}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">{t('ownership.accountManagerSection.none')}</option>
              {(opsUsers.data ?? []).map((u) => (
                <option key={u.id} value={String(u.id)}>
                  {opsUserName(u)}
                  {u.status === 'AWAY' ? ' (away)' : ''}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={save.isLoading}
              onClick={() => save.mutate(selected ? Number(selected) : null)}
            >
              {save.isLoading ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={save.isLoading}
              onClick={() => {
                setEditing(false);
                setError(null);
              }}
            >
              {t('common.actions.cancel')}
            </Button>
          </>
        ) : (
          <>
            <OwnerChip owner={current} meId={opsUser?.id} emptyLabel="Not set" />
            {isManager && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setSelected(current ? String(current.id) : '');
                  setEditing(true);
                }}
              >
                {current ? 'Change' : 'Set'}
              </Button>
            )}
          </>
        )}
        {error && (
          <span className={styles.error} role="alert">
            {error}
          </span>
        )}
        <span className={styles.hint}>
          {t('ownership.accountManagerSection.autoAssignHint')}
        </span>
      </div>
    </div>
  );
}
